import React from 'react'

/**
 * A custom React hook that provides a mechanism to handle loading states with a delay.
 *
 * @param {Object} options - Configuration options for the hook.
 * @param {number} [options.delay=2000] - The delay in milliseconds before the loading state is activated.
 *
 * @returns {Object} An object containing the following properties:
 *  - `disabled`: A boolean indicating whether the loading process is disabled.
 *  - `loading`: A boolean representing the current loading state.
 *  - `startLoading`: A function to initiate the loading process with the specified delay.
 *  - `stopLoading`: A function to stop the loading process immediately.
 *
 * The hook manages a loading state, which can be triggered after a specified delay. It also provides
 * cleanup to clear the timeout if the component is unmounted while a loading process is in progress.
 */

export function useLoadingDelay({ delay = 2000 }: { delay?: number } = {}) {
  const [disabled, setDisabled] = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  let loadingTimeout: NodeJS.Timeout | null = null

  const startLoading = React.useCallback(() => {
    setDisabled(true)
    loadingTimeout = setTimeout(() => setLoading(true), delay)
  }, [delay])

  const stopLoading = React.useCallback(() => {
    if (loadingTimeout) clearTimeout(loadingTimeout)
    setDisabled(false)
    setLoading(false)
  }, [loadingTimeout])

  React.useEffect(() => {
    return () => {
      if (loadingTimeout) clearTimeout(loadingTimeout)
    }
  }, [loadingTimeout])

  return {
    disabled,
    loading,
    startLoading,
    stopLoading,
  }
}
